import type { Recordable, UserInfo } from '@vben/types';

import type { AuthApi } from '#/api';

import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { preferences } from '@vben/preferences';
import { resetAllStores, useAccessStore, useUserStore } from '@vben/stores';

import { useStorage } from '@vueuse/core';
import { ElNotification } from 'element-plus';
import { defineStore } from 'pinia';

import {
  distributorLoginPhoneApi,
  distributorLoginSmsCodeApi,
  getDistributorInfoApi,
  getInfoApi,
  loginApi,
  loginPhoneApi,
  loginSmsCodeApi,
  logoutApi,
} from '#/api';
import { prewarmTiktokDataApi } from '#/api/tiktok-operations';
import { LOGIN_PATH } from '#/config';
import { $t } from '#/locales';
import {
  currentHostSubdomainPrefix,
  useDistributorBrandStore,
} from '#/store/brand';
import { useDicEnumStore } from '#/store/dicEnum';
import {
  rememberFinanceLoginIdentity,
  resolveFinanceLoginIdentity,
} from '#/utils/distributor-finance-route';

type UserIdentity = 'distributor' | 'merchant';

let merchantTiktokPrewarmTriggered = false;

export const useAuthStore = defineStore('auth', () => {
  const accessStore = useAccessStore();
  const userStore = useUserStore();
  const dicEnumStore = useDicEnumStore();

  const router = useRouter();

  const loginLoading = ref(false);

  /**
   * 异步处理登录操作
   * Asynchronously handle the login process
   * @param params 登录表单数据
   */
  async function authLogin(
    params: Recordable<any>,
    onSuccess?: () => Promise<void> | void,
  ) {
    // 异步处理用户登录操作并获取 accessToken
    let userInfo: null | UserInfo = null;
    try {
      loginLoading.value = true;
      const { accessToken } = await loginApi(params);

      // 如果成功获取到 accessToken
      if (accessToken) {
        // 将 accessToken 存储到 accessStore 中
        accessStore.setAccessToken(accessToken);

        // 获取用户信息并存储到 accessStore 中
        const fetchUserInfoResult = await fetchUserInfo();

        userInfo = fetchUserInfoResult;

        userStore.setUserInfo(userInfo);
        // accessStore.setAccessCodes(accessCodes);

        if (accessStore.loginExpired) {
          accessStore.setLoginExpired(false);
        } else {
          onSuccess
            ? await onSuccess?.()
            : await router.push(
                userInfo.homePath || preferences.app.defaultHomePath,
              );
        }

        if (userInfo?.realName) {
          ElNotification({
            message: `${$t('authentication.loginSuccessDesc')}:${userInfo?.realName}`,
            title: $t('authentication.loginSuccess'),
            type: 'success',
          });
        }
      }
    } finally {
      loginLoading.value = false;
    }

    return {
      userInfo,
    };
  }

  async function logout(redirect: boolean = true) {
    // 退出前先取分销商域名前缀（resetAllStores 会清空 brand store；本地用 query 模拟需透传，真实三级域名 hostname 不变其实无需） // AI-Generated by Claude · 分销商多域名登录 T7
    // store 可能未加载过品牌（工作台未触发 loadByCurrentHost），兜底从当前 URL 重新识别 // AI-Generated by Claude · 分销商多域名登录 T7
    const subdomainPrefix =
      useDistributorBrandStore().subdomainPrefix ||
      currentHostSubdomainPrefix();
    try {
      await logoutApi();
    } catch {
      // 不做任何处理
    }
    // resetAllStores 对 setup 风格 store 调 $reset 会抛错并中断后续跳转，包裹兜底 // AI-Generated by Claude · 分销商多域名登录 T7
    try {
      resetAllStores();
    } catch {
      // 忽略 $reset 不支持的报错，保证后续跳转执行
    }
    // 显式清除 token，避免 resetAllStores 中途抛错漏清导致退出不彻底 // AI-Generated by Claude · 分销商多域名登录 T7
    accessStore.setAccessToken(null);
    accessStore.setLoginExpired(false);

    // 退出落地：分销商域名→登录页(分销商品牌)；总平台→官网首页 LOGIN_PATH(保留原设计) // AI-Generated by Claude · 分销商多域名登录 T7
    const landingPath = subdomainPrefix ? '/auth/login' : LOGIN_PATH;
    const query: Record<string, string> = {};
    if (redirect) {
      query.redirect = encodeURIComponent(router.currentRoute.value.fullPath);
    }
    if (subdomainPrefix) {
      query.subdomainPrefix = subdomainPrefix;
    }
    await router.replace({
      path: landingPath,
      query,
    });
  }

  async function fetchUserInfo(
    getDict = true,
    identity: UserIdentity = 'merchant',
  ) {
    let userInfo: null | UserInfo = null;
    userInfo =
      identity === 'distributor'
        ? await getDistributorInfoApi()
        : await getInfoApi();
    userInfo = normalizeUserInfo(userInfo, identity);
    rememberFinanceLoginIdentity(identity);
    userStore.setUserInfo(userInfo);
    useStorage('userInfo', { id: userInfo?.id, name: userInfo?.name });

    getDict && identity === 'merchant' && (await getDictionaryList());

    if (identity === 'merchant' && !merchantTiktokPrewarmTriggered) {
      merchantTiktokPrewarmTriggered = true;
      void prewarmTiktokDataApi().catch(() => {
        // Login and route initialization must never wait for TikTok. A later
        // user-info refresh may retry if the background trigger was unavailable.
        merchantTiktokPrewarmTriggered = false;
      });
    }

    return userInfo;
  }

  async function getDictionaryList() {
    await dicEnumStore.getDictionaryList();
  }

  function getLoginIdentity(): UserIdentity {
    const currentRoute = router.currentRoute.value;
    return resolveFinanceLoginIdentity(
      currentRoute.path,
      currentRoute.query?.redirect,
    ) as UserIdentity;
  }

  function buildMerchantDomainLoginUrl(redirectSubdomainPrefix?: string) {
    const targetPrefix = (redirectSubdomainPrefix ?? '').trim();
    if (typeof window === 'undefined') return '/auth/login';

    const url = new URL(window.location.href);
    url.pathname = '/auth/login';
    url.search = '';
    url.hash = '';

    const hostname = window.location.hostname.toLowerCase();
    const isLocal =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
    if (isLocal) {
      if (targetPrefix) {
        url.searchParams.set('subdomainPrefix', targetPrefix);
      }
      return url.toString();
    }

    const parts = hostname.split('.');
    if (parts.length > 1) {
      const currentFirst = parts[0] ?? '';
      const prePrefix = currentFirst.startsWith('pre-') ? 'pre-' : '';
      parts[0] = targetPrefix
        ? `${prePrefix}${targetPrefix}-b`
        : `${prePrefix}b`;
      url.hostname = parts.join('.');
    }
    return url.toString();
  }

  function handleMerchantDomainRedirect(result: AuthApi.LoginResult) {
    if (!result?.forceRedirect) return false;
    accessStore.setAccessToken(null);
    const targetUrl = buildMerchantDomainLoginUrl(
      result.redirectSubdomainPrefix,
    );
    ElNotification({
      message: result.redirectReason || '账号已注册，请切回原归属域名登录',
      title: '登录域名不匹配',
      type: 'warning',
    });
    window.location.replace(targetUrl);
    return true;
  }

  function normalizeUserInfo(userInfo: UserInfo, identity: UserIdentity) {
    if (identity !== 'distributor') {
      return userInfo;
    }

    return {
      ...userInfo,
      homePath: userInfo.homePath || '/workspace/finance/recharge',
      roles: [...new Set(['distributor', ...(userInfo.roles ?? [])])],
    };
  }

  function $reset() {
    loginLoading.value = false;
    merchantTiktokPrewarmTriggered = false;
  }

  async function loginSmsCode(
    params: Recordable<any>,
    onSuccess?: () => Promise<void> | void,
  ) {
    // 异步处理用户登录操作并获取 accessToken
    let userInfo: null | UserInfo = null;
    try {
      loginLoading.value = true;
      const identity = getLoginIdentity();
      const loginParams =
        identity === 'distributor'
          ? params
          : useDistributorBrandStore().appendRegistrationContext({ ...params });
      const loginResult =
        identity === 'distributor'
          ? await distributorLoginSmsCodeApi(loginParams)
          : await loginSmsCodeApi(loginParams);
      if (
        identity === 'merchant' &&
        handleMerchantDomainRedirect(loginResult)
      ) {
        return { userInfo };
      }
      const { token } = loginResult;
      const accessToken = token;
      // 如果成功获取到 accessToken
      if (accessToken) {
        // 将 accessToken 存储到 accessStore 中
        accessStore.setAccessToken(accessToken);

        // 获取用户信息并存储到 accessStore 中
        const fetchUserInfoResult = await fetchUserInfo(true, identity);

        userInfo = fetchUserInfoResult;

        userStore.setUserInfo(userInfo);
        // accessStore.setAccessCodes(accessCodes);

        if (accessStore.loginExpired) {
          accessStore.setLoginExpired(false);
        } else {
          onSuccess
            ? await onSuccess?.()
            : await router.push(
                userInfo.homePath || preferences.app.defaultHomePath,
              );
        }

        if (userInfo?.realName) {
          ElNotification({
            message: `${$t('authentication.loginSuccessDesc')}:${userInfo?.realName}`,
            title: $t('authentication.loginSuccess'),
            type: 'success',
          });
        }
      }
    } finally {
      loginLoading.value = false;
    }

    return {
      userInfo,
    };
  }

  async function loginPhone(
    params: Recordable<any>,
    onSuccess?: () => Promise<void> | void,
  ) {
    // 异步处理用户登录操作并获取 accessToken
    let userInfo: null | UserInfo = null;
    try {
      loginLoading.value = true;
      const identity = getLoginIdentity();
      const loginParams =
        identity === 'distributor'
          ? params
          : useDistributorBrandStore().appendRegistrationContext({ ...params }); // AI-Generated by Claude · 分销商多域名登录
      const loginResult =
        identity === 'distributor'
          ? await distributorLoginPhoneApi(loginParams)
          : await loginPhoneApi(loginParams);
      if (
        identity === 'merchant' &&
        handleMerchantDomainRedirect(loginResult)
      ) {
        return { userInfo };
      }
      const { token } = loginResult;
      const accessToken = token;
      // 如果成功获取到 accessToken
      if (accessToken) {
        // 将 accessToken 存储到 accessStore 中
        accessStore.setAccessToken(accessToken);

        // 获取用户信息并存储到 accessStore 中
        const fetchUserInfoResult = await fetchUserInfo(true, identity);

        userInfo = fetchUserInfoResult;

        userStore.setUserInfo(userInfo);
        // accessStore.setAccessCodes(accessCodes);

        if (accessStore.loginExpired) {
          accessStore.setLoginExpired(false);
        } else {
          onSuccess
            ? await onSuccess?.()
            : await router.push(
                userInfo.homePath || preferences.app.defaultHomePath,
              );
        }

        if (userInfo?.realName) {
          ElNotification({
            message: `${$t('authentication.loginSuccessDesc')}:${userInfo?.realName}`,
            title: $t('authentication.loginSuccess'),
            type: 'success',
          });
        }
      }
    } finally {
      loginLoading.value = false;
    }

    return {
      userInfo,
    };
  }
  return {
    $reset,
    authLogin,
    fetchUserInfo,
    loginLoading,
    logout,
    loginSmsCode,
    loginPhone,
  };
});
